import * as React from 'react'

import { StatusText } from '../feedback/status-text'
import { cn } from '../lib/cn'
import { FieldError } from './field-error'
import { Label } from './label'

// The control arrives as a function, not as `children` to clone: the ids behind
// `aria-describedby` / `aria-invalid` must land on *that* control, and typed beats guessed.

export interface FormFieldControlProps {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': boolean | undefined
  'aria-required': boolean | undefined
}

export interface FormFieldProps {
  /** The label's text, already translated by the app's message layer. */
  label: string
  /** The control, which receives the wiring attributes to apply. */
  children: (props: FormFieldControlProps) => React.ReactNode
  /** Help text below the control. */
  description?: string
  /** Error message: when present, the field becomes invalid. */
  error?: string
  /** What looks wrong with the value as it stands, which does not block: the field stays
   *  valid and the line is read with it, not announced. An `error` takes its place. */
  warning?: string
  /** Marks the field as required, for screen readers and with the asterisk. */
  required?: boolean
  /** The control's id: a stable one is generated when it is absent. */
  id?: string
  className?: string
}

export function FormField({
  label,
  children,
  description,
  error,
  warning,
  required = false,
  id,
  className,
}: FormFieldProps) {
  const generatedId = React.useId()
  const controlId = id ?? generatedId
  const descriptionId = `${controlId}-description`
  const errorId = `${controlId}-error`
  const warningId = `${controlId}-warning`
  const shownWarning = error ? undefined : warning

  const describedBy = [
    description ? descriptionId : null,
    shownWarning ? warningId : null,
    error ? errorId : null,
  ].filter((value): value is string => value !== null)

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Label htmlFor={controlId}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-destructive">
            *
          </span>
        ) : null}
      </Label>

      {children({
        id: controlId,
        'aria-describedby': describedBy.length > 0 ? describedBy.join(' ') : undefined,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })}

      {description ? (
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}

      {shownWarning ? (
        <StatusText id={warningId} tone="warning">
          {shownWarning}
        </StatusText>
      ) : null}

      <FieldError id={errorId}>{error}</FieldError>
    </div>
  )
}
